'use strict';

const libPictProvider = require('pict-provider');
const libUserSearch = require('../access/User-Search.js');

// Shared kind registry + per-type config model (single source of truth with the #/CertificationTypes screen).
const libCertConfig = require('../Certification-Config.js');
const HOLDER_KINDS = libCertConfig.HOLDER_KINDS;
const HOLDER_KIND_BY_TYPE = libCertConfig.HOLDER_KIND_BY_TYPE;
const APPLIES_TO_KIND_LIST = libCertConfig.APPLIES_TO_KIND_LIST;

// Mount points (the Certifications view lays these out; this provider fills them with hand-built HTML — the
// Entitlements/SuperUserGrant idiom).
const RAIL_MOUNT = '#Cert-Rail';
const RAIL_LIST_MOUNT = '#Cert-Rail-List';
const DETAIL_MOUNT = '#Cert-Detail';
// Picker mounts (Pict-Section-Picker). The certify (holder) picker is multi; the applies-to picker is single.
const HOLDER_PICKER_MOUNT = '#Cert-HolderPicker';
const APPLIES_PICKER_MOUNT = '#Cert-AppliesPicker';

/**
 * Certifications — the #/Certifications workspace. Pick (or create) a CertificationType on the left, then on the
 * right: BULK-CERTIFY holders — pick a holder KIND (User, Material, Lab, Equipment, Test, …), set the dates once,
 * multi-pick entities → one Certification + CertificationEntityPolyJoin per entity; see + search + remove current
 * holders (any kind); and manage what the type "certifies for" (Material/Lab applicability, via
 * CertificationTypeCertifiedEntityPolyJoin).
 *
 * Faithful to the Home app's model: a Certification is ONE row per holder (never shared) carrying the optional
 * CertifiedOn / CertifiedStartDate / CertifiedEndDate; the holder link is a polymorphic CertificationEntityPolyJoin
 * {EntityType, IDEntity} (User/Material/Lab/Equipment/Test/MixDesign/…). The client never sends IDCustomer (the
 * server stamps it from the session).
 */
class CertificationsProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this._typeSearchTimer = null;
		this._typeSearchToken = 0;
		this._holderSearchTimer = null;
		this._holderSearchToken = 0;
	}

	get restClient() { return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient; }
	callerCustomer() { const tmpUser = this.pict.AppData.CurrentUser; return tmpUser ? Number(tmpUser.CustomerID) : 0; }

	/** UI gate — creating certifications needs Certification-Create (server stays authoritative). */
	canEdit()
	{
		const tmpApp = this.pict.PictApplication;
		if (tmpApp && tmpApp.EffectivePermissionsService && (typeof tmpApp.EffectivePermissionsService.can === 'function'))
		{
			return tmpApp.EffectivePermissionsService.can('Certification', 'Create');
		}
		return true;   // before the permission map loads, don't hide the UI (server enforces)
	}

	get state()
	{
		if (!this.pict.AppData.Certifications)
		{
			this.pict.AppData.Certifications =
			{
				Loaded: false, Loading: false,
				Types: [], TypeSearch: '',
				SelectedTypeID: 0, SelectedType: null,
				// per-type config (from the Configuration service) — which kinds this type certifies + certifies-for + defaults
				TypeConfig: null,
				// certify form — CertifyKind picks the holder entity kind; AddEntityIDs are the picked entity ids of that kind
				CertifyKind: 'User', AddEntityIDs: [], CertOn: '', CertStart: '', CertEnd: '', Notes: '', CertifySaving: false, CertifyNotice: '', CertifyError: '',
				// holders — server-side paginated (browse), or cross-entity name search
				Holders: [], HoldersLoading: false, HolderNames: {},
				HolderPage: 0, HolderPageSize: 50, HolderTotalCount: 0, HolderSearch: '',
				// certifies-for (polymorphic: Material/Lab/Test/Equipment/…)
				AppliesTo: [], AppliesToLoading: false, AppliesToNames: {}, AppliesToBusy: false, AppliesToError: '',
				AppliesKind: 'Material', AppliesPickValue: '',
				// new-type
				ShowNewType: false, NewTypeName: '', NewTypeVerb: '', NewTypeHash: '', NewTypeDescription: '', NewTypeSaving: false, NewTypeError: '',
			};
		}
		return this.pict.AppData.Certifications;
	}

	// ── REST helpers ─────────────────────────────────────────────────────────────
	_getJSON(pURL) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.getJSON(pURL, (pErr, pBody) => resolve(pErr ? null : pBody))) : Promise.resolve(null); }
	_postJSON(pURL, pBody) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.postJSON(pURL, pBody, (pErr, pRes) => resolve({ error: pErr, result: pRes }))) : Promise.resolve({ error: 'no client' }); }
	_putJSON(pURL, pBody) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.putJSON(pURL, pBody, (pErr, pRes) => resolve({ error: pErr, result: pRes }))) : Promise.resolve({ error: 'no client' }); }
	_deleteEntity(pEntity, pID) { const tmpRC = this.restClient; return (tmpRC && (typeof tmpRC.deleteEntity === 'function')) ? new Promise((resolve) => tmpRC.deleteEntity(pEntity, pID, (pErr) => resolve(!pErr))) : Promise.resolve(false); }

	// ── Load ─────────────────────────────────────────────────────────────────────
	loadAsync()
	{
		const tmpState = this.state;
		if (tmpState.Loading) { return; }
		tmpState.Loading = true;
		this.render();
		// Certification types are a bounded per-customer catalog (dozens); load them all, sorted, for the rail.
		this._getJSON(`CertificationTypes/FilteredTo/FBV~Deleted~EQ~0~FSF~Hash~ASC~0/0/2000`).then((pRows) =>
		{
			tmpState.Types = (Array.isArray(pRows) ? pRows : []).slice();
			tmpState.Loaded = true;
			tmpState.Loading = false;
			this.render();
		});
	}

	_typeLabel(pType) { if (!pType) { return ''; } return String(pType.Name || pType.Hash || `Type ${pType.IDCertificationType}`); }
	_typeSub(pType) { if (!pType) { return ''; } const tmpBits = [ pType.Verb, (pType.Name ? pType.Hash : '') ].filter((pBit) => pBit); return tmpBits.join(' · '); }

	setTypeSearch(pValue)
	{
		this.state.TypeSearch = String(pValue == null ? '' : pValue);
		// Types are fully loaded → filter client-side; just repaint the list (keeps the search box focused).
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA.getElement(RAIL_LIST_MOUNT).length > 0) { tmpCA.assignContent(RAIL_LIST_MOUNT, this._railListHTML()); }
		else { this.render(); }
	}

	selectType(pIDCertificationType)
	{
		const tmpState = this.state;
		const tmpID = Number(pIDCertificationType);
		tmpState.SelectedTypeID = tmpID;
		tmpState.SelectedType = (tmpState.Types || []).find((pType) => Number(pType.IDCertificationType) === tmpID) || null;
		tmpState.ShowNewType = false;
		// reset the certify form + sections (keep the chosen holder kind so it sticks while browsing types)
		tmpState.TypeConfig = null;
		tmpState.AddEntityIDs = []; tmpState.CertOn = ''; tmpState.CertStart = ''; tmpState.CertEnd = ''; tmpState.Notes = '';
		tmpState.CertifyNotice = ''; tmpState.CertifyError = '';
		tmpState.Holders = []; tmpState.HolderNames = {}; tmpState.HolderPage = 0; tmpState.HolderTotalCount = 0; tmpState.HolderSearch = '';
		tmpState.AppliesTo = []; tmpState.AppliesToNames = {}; tmpState.AppliesToError = ''; tmpState.AppliesPickValue = '';
		this.render();
		if (tmpID > 0) { this._loadTypeConfigAsync(tmpID, tmpState.SelectedType); this._loadHoldersAsync(tmpID); this._loadAppliesToAsync(tmpID); }
	}

	/** Load the type's per-type config (cascaded read), normalize over defaults, and pre-scope the kind dropdowns. */
	_loadTypeConfigAsync(pTypeID, pType)
	{
		const tmpState = this.state;
		this._postJSON('Configuration/Get', { LevelContext: 'Customer', LevelContextID: this.callerCustomer(), NamespaceKey: libCertConfig.CERT_CONFIG_NAMESPACE, NamespaceValue: libCertConfig.configNamespaceValue(pTypeID) }).then((pRes) =>
		{
			if (Number(tmpState.SelectedTypeID) !== Number(pTypeID)) { return; }
			const tmpBody = (pRes && (pRes.result !== undefined)) ? pRes.result : pRes;
			const tmpRaw = (tmpBody && (tmpBody.Config !== undefined)) ? tmpBody.Config : ((tmpBody && tmpBody.Document) || tmpBody);
			const tmpConfig = libCertConfig.normalizeConfig(tmpRaw, pType);
			tmpState.TypeConfig = tmpConfig;
			// pre-scope only when an admin has EXPLICITLY configured this type — otherwise stay fully flexible (nothing
			// hidden). If the current kind isn't offered by the saved config, snap to the first configured kind.
			if (tmpConfig.Configured)
			{
				if (tmpConfig.HolderKinds.indexOf(tmpState.CertifyKind) === -1) { tmpState.CertifyKind = tmpConfig.HolderKinds[0]; }
				if (tmpConfig.AppliesToKinds.indexOf(tmpState.AppliesKind) === -1) { tmpState.AppliesKind = tmpConfig.AppliesToKinds[0]; }
			}
			this.render();
		});
	}

	/** Holder kinds offered for the selected type — the saved config if the type is CONFIGURED, else every certifiable kind. */
	_certifyKindChoices() { const tmpCfg = this.state.TypeConfig; return (tmpCfg && tmpCfg.Configured && tmpCfg.HolderKinds.length) ? tmpCfg.HolderKinds : libCertConfig.HOLDER_CERTIFIABLE_LIST; }
	/** Certifies-for kinds offered for the selected type — the saved config if CONFIGURED, else the full list. */
	_appliesKindChoices() { const tmpCfg = this.state.TypeConfig; return (tmpCfg && tmpCfg.Configured && tmpCfg.AppliesToKinds.length) ? tmpCfg.AppliesToKinds : APPLIES_TO_KIND_LIST; }

	// ── Certify users (bulk) ─────────────────────────────────────────────────────

	setNotes(pValue) { this.state.Notes = String(pValue == null ? '' : pValue); }
	setCertDate(pField, pValue)
	{
		const tmpState = this.state;
		if (pField === 'On') { tmpState.CertOn = pValue || ''; }
		else if (pField === 'Start') { tmpState.CertStart = pValue || ''; }
		else if (pField === 'End') { tmpState.CertEnd = pValue || ''; }
	}

	/** Preset: set the end date to N years after the start (or CertifiedOn, or today) — mirrors the Home app helper. */
	applyDatePreset(pYears)
	{
		const tmpState = this.state;
		const tmpBase = tmpState.CertStart || tmpState.CertOn;
		if (!tmpBase) { tmpState.CertifyError = 'Set a start date first, then apply a preset.'; this.render(); return; }
		const tmpParts = String(tmpBase).split('-').map((pPart) => Number(pPart));
		if (tmpParts.length !== 3) { return; }
		const tmpEndYear = tmpParts[0] + Number(pYears);
		tmpState.CertEnd = `${tmpEndYear}-${String(tmpParts[1]).padStart(2, '0')}-${String(tmpParts[2]).padStart(2, '0')}`;
		tmpState.CertifyError = '';
		this.render();
	}

	/** YYYY-MM-DD (from a date input) → an ISO DateTime the API stores; '' → null. */
	_dateToISO(pYMD) { const tmpV = String(pYMD || '').trim(); return tmpV ? `${tmpV}T00:00:00.000Z` : null; }
	/** ISO DateTime → YYYY-MM-DD for a date input / display. */
	_isoToYMD(pISO) { const tmpV = String(pISO || ''); return (tmpV.length >= 10) ? tmpV.slice(0, 10) : ''; }

	/** Change the holder kind being certified (User / Material / Lab / …) — clears the picked entities + re-mounts. */
	setCertifyKind(pValue)
	{
		const tmpState = this.state;
		const tmpKind = HOLDER_KIND_BY_TYPE[pValue] ? String(pValue) : 'User';
		if (tmpState.CertifyKind === tmpKind) { return; }
		tmpState.CertifyKind = tmpKind;
		tmpState.AddEntityIDs = [];   // ids are kind-specific — drop the previous kind's selection
		tmpState.CertifyError = '';
		this.mountCertifyPicker();     // re-create the picker (fresh chips + kind-specific placeholder + data provider)
	}

	/**
	 * Bulk-certify: for each picked entity of the chosen kind, create a Certification (this type + the shared dates +
	 * notes) then attach it via a CertificationEntityPolyJoin{EntityType:<kind>, IDEntity:<id>}. One Certification per
	 * holder (never shared), so each holder's dates can be renewed independently. IDCustomer is server-derived.
	 */
	async certifySelectedAsync()
	{
		const tmpState = this.state;
		if (!this.canEdit() || tmpState.CertifySaving) { return; }
		const tmpTypeID = Number(tmpState.SelectedTypeID);
		const tmpKindDef = HOLDER_KIND_BY_TYPE[tmpState.CertifyKind] || HOLDER_KIND_BY_TYPE['User'];
		const tmpIDs = (tmpState.AddEntityIDs || []).map(Number).filter((pID) => pID > 0);
		if (!(tmpTypeID > 0)) { return; }
		if (!tmpIDs.length)
		{
			tmpState.CertifyError = `Search for and pick one or more ${tmpKindDef.Ph.replace(/ by .*$/, '')} to certify first.`;
			tmpState.CertifyNotice = '';
			this.render();
			return;
		}
		if (tmpState.CertStart && tmpState.CertEnd && (tmpState.CertStart > tmpState.CertEnd)) { tmpState.CertifyError = 'The end date is before the start date.'; tmpState.CertifyNotice = ''; this.render(); return; }
		tmpState.CertifySaving = true; tmpState.CertifyError = ''; tmpState.CertifyNotice = '';
		this.render();
		// Only send dates that are actually set (omit empties rather than sending null, which the server may reject).
		const tmpCertBody = { IDCertification: 0, IDCertificationType: tmpTypeID, Notes: String(tmpState.Notes || '') };
		const tmpOn = this._dateToISO(tmpState.CertOn); if (tmpOn) { tmpCertBody.CertifiedOn = tmpOn; }
		const tmpStartISO = this._dateToISO(tmpState.CertStart); if (tmpStartISO) { tmpCertBody.CertifiedStartDate = tmpStartISO; }
		const tmpEndISO = this._dateToISO(tmpState.CertEnd); if (tmpEndISO) { tmpCertBody.CertifiedEndDate = tmpEndISO; }
		let tmpOK = 0;
		let tmpFail = 0;
		const tmpConcurrency = 5;
		for (let tmpStart = 0; tmpStart < tmpIDs.length; tmpStart += tmpConcurrency)
		{
			const tmpBatch = tmpIDs.slice(tmpStart, tmpStart + tmpConcurrency);
			/* eslint-disable no-await-in-loop */
			await Promise.all(tmpBatch.map((pID) => this._certifyOne(tmpCertBody, tmpKindDef.Kind, pID).then((pOk) => { if (pOk) { tmpOK++; } else { tmpFail++; } })));
			/* eslint-enable no-await-in-loop */
		}
		const tmpNoun = tmpOK === 1 ? 'holder' : 'holders';
		tmpState.CertifySaving = false;
		tmpState.AddEntityIDs = [];
		tmpState.CertifyNotice = tmpFail
			? `Certified ${tmpOK} ${tmpNoun}; ${tmpFail} failed (you may lack permission).`
			: `Certified ${tmpOK} ${tmpNoun}.`;
		// Repaint the certify panel FIRST so the "certifying…" busy indicator clears and the notice shows —
		// `_reloadHolders` only repaints the holders list body (`_renderHoldersBody`), not this panel, so on its
		// own it would leave the CertifySaving span stuck next to the button even though CertifySaving is false.
		this.render();
		this._reloadHolders();   // then reload + repaint the holders list to include the new certifications
	}

	/** One holder: POST Certification then POST its poly-join {EntityType, IDEntity}. Resolves true on success. */
	_certifyOne(pCertBody, pEntityType, pIDEntity)
	{
		return this._postJSON('Certification', Object.assign({}, pCertBody)).then((pRes) =>
		{
			const tmpCert = pRes && pRes.result;
			if (pRes.error || !tmpCert || !tmpCert.IDCertification) { return false; }
			return this._postJSON('CertificationEntityPolyJoin', { IDCertificationEntityPolyJoin: 0, IDCertification: Number(tmpCert.IDCertification), EntityType: String(pEntityType), IDEntity: Number(pIDEntity) })
				.then((pJoinRes) => !pJoinRes.error);
		});
	}

	// ── Holders (users who hold this type) ───────────────────────────────────────

	// Holders of a type = the User poly-joins of this type's Certifications. BROWSE mode pages the type's
	// Certifications server-side (begin/count) → their User poly-joins → holders. A name query switches to SEARCH mode
	// (find users by name, login ID or email, keep those holding this type). The `?Joins=` inline param is not honored by the
	// server, so every mode reads certs + poly-joins separately and joins them client-side. The holders body repaints
	// on its own mount (#Cert-Holders-Body) so the search box keeps focus and the certify/applies-to pickers don't
	// re-mount on a keystroke or a page turn.

	setHolderSearch(pValue)
	{
		this.state.HolderSearch = String(pValue == null ? '' : pValue);
		if (this._holderSearchTimer) { clearTimeout(this._holderSearchTimer); }
		this._holderSearchTimer = setTimeout(() => { this._holderSearchTimer = null; this._runHolderQuery(); }, 250);
	}

	/** New search-box value → name search (≥2 chars) or browse page 0. */
	_runHolderQuery()
	{
		const tmpState = this.state;
		const tmpTypeID = Number(tmpState.SelectedTypeID);
		if (!(tmpTypeID > 0)) { return; }
		const tmpTerm = String(tmpState.HolderSearch || '').trim();
		if (tmpTerm.length >= 2) { this._searchHoldersAsync(tmpTypeID, tmpTerm); }
		else { this._loadHoldersAsync(tmpTypeID, 0); }
	}

	/** Reload the current holders view (after certify / remove) — same page, or re-run the active search. */
	_reloadHolders()
	{
		const tmpState = this.state;
		const tmpTypeID = Number(tmpState.SelectedTypeID);
		if (!(tmpTypeID > 0)) { return; }
		const tmpTerm = String(tmpState.HolderSearch || '').trim();
		if (tmpTerm.length >= 2) { this._searchHoldersAsync(tmpTypeID, tmpTerm); }
		else { this._loadHoldersAsync(tmpTypeID, tmpState.HolderPage); }
	}

	holderPrevPage() { const tmpState = this.state; if (tmpState.HolderPage > 0) { this._loadHoldersAsync(Number(tmpState.SelectedTypeID), tmpState.HolderPage - 1); } }
	holderNextPage() { const tmpState = this.state; const tmpPages = Math.max(1, Math.ceil(tmpState.HolderTotalCount / tmpState.HolderPageSize)); if (tmpState.HolderPage < (tmpPages - 1)) { this._loadHoldersAsync(Number(tmpState.SelectedTypeID), tmpState.HolderPage + 1); } }

	/** BROWSE: page the type's Certifications (begin/count, stable sort), then their User poly-joins → holders. */
	_loadHoldersAsync(pTypeID, pPage)
	{
		const tmpState = this.state;
		const tmpPage = Math.max(0, Number(pPage) || 0);
		tmpState.HolderPage = tmpPage;
		tmpState.HoldersLoading = true;
		this._renderHoldersBody();
		const tmpScope = `FBV~IDCertificationType~EQ~${pTypeID}~FBV~Deleted~EQ~0`;
		const tmpStart = tmpPage * tmpState.HolderPageSize;
		Promise.all(
			[
				this._getJSON(`Certifications/FilteredTo/${tmpScope}~FSF~IDCertification~ASC~0/${tmpStart}/${tmpState.HolderPageSize}`),
				(tmpPage === 0) ? this._getJSON(`Certifications/Count/FilteredTo/${tmpScope}`) : Promise.resolve(null),
			]).then((pResults) =>
		{
			if (Number(tmpState.SelectedTypeID) !== Number(pTypeID)) { return; }
			if (pResults[1] && (pResults[1].Count != null)) { tmpState.HolderTotalCount = Number(pResults[1].Count); }
			this._buildHoldersFromCerts(pTypeID, Array.isArray(pResults[0]) ? pResults[0] : []);
		});
	}

	/** Resolve a page of Certifications → holders (their User poly-joins), then repaint + resolve names. */
	_buildHoldersFromCerts(pTypeID, pCerts)
	{
		const tmpState = this.state;
		const tmpCertByID = {};
		pCerts.forEach((pCert) => { tmpCertByID[Number(pCert.IDCertification)] = pCert; });
		const tmpIDs = Object.keys(tmpCertByID);
		if (!tmpIDs.length) { tmpState.Holders = []; tmpState.HoldersLoading = false; this._renderHoldersBody(); return; }
		// Any holder kind (User/Material/Lab/…) — one poly-join per Certification; join client-side.
		this._getJSON(`CertificationEntityPolyJoins/FilteredTo/FBL~IDCertification~INN~${tmpIDs.join(',')}~FBV~Deleted~EQ~0/0/${tmpIDs.length + 10}`).then((pJoins) =>
		{
			if (Number(tmpState.SelectedTypeID) !== Number(pTypeID)) { return; }
			tmpState.Holders = (Array.isArray(pJoins) ? pJoins : []).map((pJoin) => this._holderFrom(pJoin, tmpCertByID[Number(pJoin.IDCertification)])).filter((pRow) => pRow);
			tmpState.HoldersLoading = false;
			this._renderHoldersBody();
			this._resolveHolderNames();
		});
	}

	_holderFrom(pJoin, pCert)
	{
		if (!pJoin || !pCert) { return null; }
		return { IDCertification: Number(pJoin.IDCertification), IDJoin: Number(pJoin.IDCertificationEntityPolyJoin), EntityType: String(pJoin.EntityType || ''), IDEntity: Number(pJoin.IDEntity), On: this._isoToYMD(pCert.CertifiedOn), Start: this._isoToYMD(pCert.CertifiedStartDate), End: this._isoToYMD(pCert.CertifiedEndDate) };
	}

	/**
	 * SEARCH (polymorphic): match the term against every holder kind's entity set in parallel (one request per kind,
	 * see _kindFilter), collect matching entities per kind, find their poly-joins per kind, then keep only the
	 * ones whose Certification is of this type.
	 */
	_searchHoldersAsync(pTypeID, pTerm)
	{
		const tmpState = this.state;
		tmpState.HoldersLoading = true;
		this._renderHoldersBody();
		const tmpToken = ++this._holderSearchToken;
		const fStale = () => (tmpToken !== this._holderSearchToken) || (Number(tmpState.SelectedTypeID) !== Number(pTypeID));
		// 1) matching entities of every holder kind (User is customer-scoped; the rest are session-scoped by the server)
		Promise.all(HOLDER_KINDS.map((pKind) =>
		{
			const tmpScope = (pKind.Kind === 'User') ? `~FBV~IDCustomer~EQ~${this.callerCustomer()}` : '';
			return this._getJSON(`${pKind.Plural}/FilteredTo/${this._kindFilter(pKind, pTerm)}${tmpScope}/0/25`)
				.then((pRows) =>
				{
					const tmpByID = {};
					(Array.isArray(pRows) ? pRows : []).forEach((pRec) =>
					{
						const tmpID = Number(pRec && pRec[pKind.IDField]);
						if (tmpID > 0)
						{
							tmpByID[tmpID] = pRec;
						}
					});
					return { Kind: pKind, Records: tmpByID };
				});
		})).then((pKindResults) =>
		{
			if (fStale()) { return; }
			// stash labels + collect (kind → matched ids)
			const tmpKindIDs = [];
			pKindResults.forEach((pKR) =>
			{
				const tmpIDs = Object.keys(pKR.Records).map(Number);
				if (!tmpIDs.length) { return; }
				tmpIDs.forEach((pID) => { tmpState.HolderNames[`${pKR.Kind.Kind}:${pID}`] = this._entityLabel(pKR.Kind, pKR.Records[pID]); });
				tmpKindIDs.push({ Kind: pKR.Kind, IDs: tmpIDs });
			});
			if (!tmpKindIDs.length) { tmpState.Holders = []; tmpState.HoldersLoading = false; this._renderHoldersBody(); return; }
			// 2) poly-joins for those entities (per kind, parallel)
			Promise.all(tmpKindIDs.map((pKI) => this._getJSON(`CertificationEntityPolyJoins/FilteredTo/FBV~EntityType~EQ~${pKI.Kind.Kind}~FBL~IDEntity~INN~${pKI.IDs.join(',')}~FBV~Deleted~EQ~0/0/1000`))).then((pJoinArrays) =>
			{
				if (fStale()) { return; }
				const tmpJoinRows = [];
				pJoinArrays.forEach((pRows) => (Array.isArray(pRows) ? pRows : []).forEach((pJoin) => tmpJoinRows.push(pJoin)));
				const tmpCertIDs = Array.from(new Set(tmpJoinRows.map((pJoin) => Number(pJoin.IDCertification)).filter((pID) => pID > 0)));
				if (!tmpCertIDs.length) { tmpState.Holders = []; tmpState.HoldersLoading = false; this._renderHoldersBody(); return; }
				// 3) keep only poly-joins whose Certification is of THIS type
				this._getJSON(`Certifications/FilteredTo/FBL~IDCertification~INN~${tmpCertIDs.join(',')}~FBV~IDCertificationType~EQ~${pTypeID}~FBV~Deleted~EQ~0/0/${tmpCertIDs.length + 10}`).then((pCerts) =>
				{
					if (fStale()) { return; }
					const tmpCertByID = {};
					(Array.isArray(pCerts) ? pCerts : []).forEach((pCert) => { tmpCertByID[Number(pCert.IDCertification)] = pCert; });
					tmpState.Holders = tmpJoinRows.map((pJoin) => this._holderFrom(pJoin, tmpCertByID[Number(pJoin.IDCertification)])).filter((pRow) => pRow);
					tmpState.HoldersLoading = false;
					this._renderHoldersBody();
				});
			});
		});
	}

	/** Resolve holder entity ids → display names, grouped by EntityType (batched INN per kind), then repaint. */
	_resolveHolderNames()
	{
		const tmpState = this.state;
		const tmpByType = {};
		(tmpState.Holders || []).forEach((pHolder) =>
		{
			if (!(pHolder.IDEntity > 0)) { return; }
			if (tmpState.HolderNames[`${pHolder.EntityType}:${pHolder.IDEntity}`]) { return; }
			(tmpByType[pHolder.EntityType] = tmpByType[pHolder.EntityType] || []).push(pHolder.IDEntity);
		});
		const tmpTypes = Object.keys(tmpByType);
		if (!tmpTypes.length) { return; }
		tmpTypes.forEach((pType) =>
		{
			const tmpKindDef = HOLDER_KIND_BY_TYPE[pType];
			const tmpIDs = Array.from(new Set(tmpByType[pType])).slice(0, 500);
			if (!tmpKindDef)   // unknown holder kind — label generically so the row still renders
			{
				tmpIDs.forEach((pID) => { tmpState.HolderNames[`${pType}:${pID}`] = `${pType} #${pID}`; });
				this._renderHoldersBody();
				return;
			}
			this._getJSON(`${tmpKindDef.Plural}/FilteredTo/FBL~${tmpKindDef.IDField}~INN~${tmpIDs.join(',')}~FBV~Deleted~EQ~0/0/${tmpIDs.length}`).then((pRows) =>
			{
				(Array.isArray(pRows) ? pRows : []).forEach((pRec) => { const tmpID = Number(pRec[tmpKindDef.IDField]); tmpState.HolderNames[`${pType}:${tmpID}`] = this._entityLabel(tmpKindDef, pRec); });
				this._renderHoldersBody();
			});
		});
	}

	/** Display label for any holder-kind record (User → the shared user label; others → Name, with a typed fallback). */
	_entityLabel(pKindDef, pRecord)
	{
		if (!pRecord) { return ''; }
		if (pKindDef && pKindDef.Kind === 'User')
		{
			return libUserSearch.userLabel(pRecord);
		}
		const tmpID = pKindDef ? pRecord[pKindDef.IDField] : '';
		return String(pRecord.Name || pRecord.Title || `${pKindDef ? pKindDef.Kind : 'Entity'} #${tmpID}`);
	}

	/** Remove a holder: delete the poly-join then the Certification (mirrors the Home app). */
	async removeHolderAsync(pIDCertification, pIDJoin)
	{
		if (!this.canEdit()) { return; }
		await this._deleteEntity('CertificationEntityPolyJoin', Number(pIDJoin));
		await this._deleteEntity('Certification', Number(pIDCertification));
		this._reloadHolders();
	}

	// ── Certifies-for (polymorphic entity applicability) ─────────────────────────

	_loadAppliesToAsync(pTypeID)
	{
		const tmpState = this.state;
		tmpState.AppliesToLoading = true;
		this._getJSON(`CertificationTypeCertifiedEntityPolyJoins/FilteredTo/FBV~IDCertificationType~EQ~${pTypeID}~FBV~Deleted~EQ~0/0/2000`).then((pRows) =>
		{
			if (Number(tmpState.SelectedTypeID) !== Number(pTypeID)) { return; }
			// Keep every kind (Material/Lab/Test/Equipment/Customer/…) — display resolves what it can, generic otherwise.
			tmpState.AppliesTo = (Array.isArray(pRows) ? pRows : []).filter((pRow) => pRow && pRow.EntityType && (pRow.IDEntity != null));
			tmpState.AppliesToLoading = false;
			this.render();
			this._resolveAppliesToNames();
		});
	}

	/** Resolve certifies-for entity ids → names, grouped by EntityType (batched INN per kind), then repaint. */
	_resolveAppliesToNames()
	{
		const tmpState = this.state;
		const tmpByType = {};
		(tmpState.AppliesTo || []).forEach((pRow) =>
		{
			const tmpID = Number(pRow.IDEntity);
			if (!(tmpID > 0) || tmpState.AppliesToNames[`${pRow.EntityType}:${tmpID}`]) { return; }
			(tmpByType[pRow.EntityType] = tmpByType[pRow.EntityType] || []).push(tmpID);
		});
		Object.keys(tmpByType).forEach((pType) =>
		{
			const tmpKindDef = HOLDER_KIND_BY_TYPE[pType];
			const tmpIDs = Array.from(new Set(tmpByType[pType])).slice(0, 500);
			if (!tmpKindDef)   // unknown certifies-for kind (Customer, CertificationType, …) — generic label
			{
				tmpIDs.forEach((pID) => { tmpState.AppliesToNames[`${pType}:${pID}`] = `${pType} #${pID}`; });
				this.render();
				return;
			}
			this._getJSON(`${tmpKindDef.Plural}/FilteredTo/FBL~${tmpKindDef.IDField}~INN~${tmpIDs.join(',')}~FBV~Deleted~EQ~0/0/${tmpIDs.length}`).then((pRows) =>
			{
				(Array.isArray(pRows) ? pRows : []).forEach((pRec) => { tmpState.AppliesToNames[`${pType}:${Number(pRec[tmpKindDef.IDField])}`] = this._entityLabel(tmpKindDef, pRec); });
				this.render();
			});
		});
	}

	/** Change the certifies-for kind — clears the pick + re-mounts the applies picker (mirrors setCertifyKind). */
	setAppliesKind(pValue)
	{
		const tmpState = this.state;
		const tmpKind = (HOLDER_KIND_BY_TYPE[pValue] && (APPLIES_TO_KIND_LIST.indexOf(pValue) > -1)) ? String(pValue) : 'Material';
		if (tmpState.AppliesKind === tmpKind) { return; }
		tmpState.AppliesKind = tmpKind;
		tmpState.AppliesPickValue = '';
		tmpState.AppliesToError = '';
		this.mountAppliesPicker();
	}

	setAppliesPick(pValue) { this.state.AppliesPickValue = Number(pValue) || 0; }

	async addAppliesToAsync()
	{
		const tmpState = this.state;
		if (!this.canEdit() || tmpState.AppliesToBusy) { return; }
		const tmpTypeID = Number(tmpState.SelectedTypeID);
		const tmpKindDef = HOLDER_KIND_BY_TYPE[tmpState.AppliesKind] || HOLDER_KIND_BY_TYPE['Material'];
		const tmpKind = tmpKindDef.Kind;
		const tmpIDEntity = Number(tmpState.AppliesPickValue);
		if (!(tmpTypeID > 0) || !(tmpIDEntity > 0)) { tmpState.AppliesToError = `Pick a ${tmpKindDef.Label.toLowerCase()} to add.`; this.render(); return; }
		if ((tmpState.AppliesTo || []).some((pRow) => (pRow.EntityType === tmpKind) && (Number(pRow.IDEntity) === tmpIDEntity))) { tmpState.AppliesToError = `That ${tmpKindDef.Label.toLowerCase()} is already listed.`; this.render(); return; }
		tmpState.AppliesToBusy = true; tmpState.AppliesToError = '';
		this.render();
		const tmpRes = await this._postJSON('CertificationTypeCertifiedEntityPolyJoin', { IDCertificationTypeCertifiedEntityPolyJoin: 0, IDCertificationType: tmpTypeID, EntityType: tmpKind, IDEntity: tmpIDEntity, Note: '' });
		tmpState.AppliesToBusy = false;
		if (tmpRes.error) { tmpState.AppliesToError = 'The server rejected the change.'; this.render(); return; }
		tmpState.AppliesPickValue = '';
		this._loadAppliesToAsync(tmpTypeID);
	}

	async removeAppliesToAsync(pIDJoin)
	{
		const tmpState = this.state;
		if (!this.canEdit()) { return; }
		await this._deleteEntity('CertificationTypeCertifiedEntityPolyJoin', Number(pIDJoin));
		this._loadAppliesToAsync(Number(tmpState.SelectedTypeID));
	}

	// ── New CertificationType ────────────────────────────────────────────────────

	showNewType() { const tmpState = this.state; tmpState.ShowNewType = true; tmpState.SelectedTypeID = 0; tmpState.SelectedType = null; tmpState.NewTypeName = ''; tmpState.NewTypeVerb = ''; tmpState.NewTypeHash = ''; tmpState.NewTypeDescription = ''; tmpState.NewTypeError = ''; this.render(); }
	setNewTypeField(pField, pValue) { const tmpState = this.state; const tmpKey = 'NewType' + pField; if (Object.prototype.hasOwnProperty.call(tmpState, tmpKey)) { tmpState[tmpKey] = String(pValue == null ? '' : pValue); } }

	async createTypeAsync()
	{
		const tmpState = this.state;
		if (!this.canEdit() || tmpState.NewTypeSaving) { return; }
		const tmpName = String(tmpState.NewTypeName || '').trim();
		const tmpHash = String(tmpState.NewTypeHash || '').trim();
		if (!tmpName && !tmpHash) { tmpState.NewTypeError = 'Give the certification a name (or a hash/code).'; this.render(); return; }
		tmpState.NewTypeSaving = true; tmpState.NewTypeError = '';
		this.render();
		const tmpRes = await this._postJSON('CertificationType', { IDCertificationType: 0, Name: tmpName, Hash: tmpHash, Verb: String(tmpState.NewTypeVerb || '').trim(), Description: String(tmpState.NewTypeDescription || '') });
		tmpState.NewTypeSaving = false;
		const tmpType = tmpRes && tmpRes.result;
		if (tmpRes.error || !tmpType || !tmpType.IDCertificationType) { tmpState.NewTypeError = 'The server rejected the new certification type.'; this.render(); return; }
		tmpState.Types.push(tmpType);
		tmpState.Types.sort((pA, pB) => String(pA.Hash || pA.Name || '').localeCompare(String(pB.Hash || pB.Name || '')));
		tmpState.ShowNewType = false;
		this.selectType(Number(tmpType.IDCertificationType));
	}

	// ── Pickers (mounted per render) ─────────────────────────────────────────────

	_pickerProvider() { return this.pict.providers['Pict-Section-Picker']; }

	_certifyKindDef() { return HOLDER_KIND_BY_TYPE[this.state.CertifyKind] || HOLDER_KIND_BY_TYPE['User']; }
	_appliesKindDef() { return HOLDER_KIND_BY_TYPE[this.state.AppliesKind] || HOLDER_KIND_BY_TYPE['Material']; }

	/**
	 * The filter for a holder-kind search: people by name, login ID or email (the shared user search), everything
	 * else by its `Search` columns, every word of the term matching; not deleted. An empty term browses.
	 *
	 * @param {{ Kind: string, Search: ?Array<string> }} pKind - A HOLDER_KINDS entry.
	 * @param {any} pTerm - The search text.
	 * @returns {string} FoxHound filter stanzas.
	 */
	_kindFilter(pKind, pTerm)
	{
		const tmpSearch = (pKind.Kind === 'User') ? libUserSearch.buildUserSearchFilter(pTerm) : libUserSearch.buildSearchFilter(pKind.Search, pTerm);
		return [ tmpSearch, 'FBV~Deleted~EQ~0' ].filter(Boolean).join('~');
	}

	/** Entity-set search bound to a kind (resolved via pGetKindDef at call-time, so re-mounting re-targets it), one
	 * request per page. Users are customer-scoped; other kinds are session-scoped by the server. */
	_kindDataProvider(pGetKindDef)
	{
		const tmpPageSize = 25;
		return (pTerm, pPage) =>
		{
			const tmpKindDef = pGetKindDef() || HOLDER_KIND_BY_TYPE['User'];
			const tmpScope = (tmpKindDef.Kind === 'User') ? `~FBV~IDCustomer~EQ~${this.callerCustomer()}` : '';
			const fMap = (pRows) => (Array.isArray(pRows) ? pRows : []).map((pRec) => ({ Value: Number(pRec[tmpKindDef.IDField]), Text: this._entityLabel(tmpKindDef, pRec), Record: pRec }));
			const tmpStart = (Number(pPage) || 0) * tmpPageSize;
			return this._getJSON(`${tmpKindDef.Plural}/FilteredTo/${this._kindFilter(tmpKindDef, pTerm)}${tmpScope}/${tmpStart}/${tmpPageSize}`)
				.then((pRows) => ({ results: fMap(pRows), hasMore: (Array.isArray(pRows) && (pRows.length >= tmpPageSize)) }));
		};
	}
	_kindResolveValue(pGetKindDef)
	{
		return (pValue) =>
		{
			const tmpKindDef = pGetKindDef() || HOLDER_KIND_BY_TYPE['User'];
			const tmpID = Number(pValue);
			if (!tmpID) { return Promise.resolve(null); }
			return this._getJSON(`${tmpKindDef.One}/${tmpID}`).then((pRec) => ((pRec && pRec[tmpKindDef.IDField]) ? { Value: tmpID, Text: this._entityLabel(tmpKindDef, pRec) } : { Value: tmpID, Text: `#${tmpID}` }));
		};
	}

	/** (Re)mount just the certify (holder) multi-picker, bound to the current CertifyKind. */
	mountCertifyPicker()
	{
		const tmpPicker = this._pickerProvider();
		if (!tmpPicker || (typeof tmpPicker.createPicker !== 'function')) { return; }
		if (this.pict.ContentAssignment.getElement(HOLDER_PICKER_MOUNT).length === 0) { return; }
		const tmpKindDef = this._certifyKindDef();
		const tmpP = tmpPicker.createPicker('Cert-HolderPicker', { DestinationAddress: HOLDER_PICKER_MOUNT, ValueAddress: 'AppData.Certifications.AddEntityIDs', Mode: 'multi', Searchable: true, Placeholder: `Search ${tmpKindDef.Ph}…`, DataProvider: this._kindDataProvider(() => this._certifyKindDef()), ResolveValue: this._kindResolveValue(() => this._certifyKindDef()) });
		if (tmpP && (typeof tmpP.render === 'function')) { tmpP.render(); }
	}

	/** (Re)mount just the certifies-for single-picker, bound to the current AppliesKind. */
	mountAppliesPicker()
	{
		const tmpPicker = this._pickerProvider();
		if (!tmpPicker || (typeof tmpPicker.createPicker !== 'function')) { return; }
		if (this.pict.ContentAssignment.getElement(APPLIES_PICKER_MOUNT).length === 0) { return; }
		const tmpKindDef = this._appliesKindDef();
		const tmpP = tmpPicker.createPicker('Cert-AppliesPicker',
			{
				DestinationAddress: APPLIES_PICKER_MOUNT, ValueAddress: 'AppData.Certifications.AppliesPickValue', Searchable: true, AllowClear: true,
				ClearLabel: `— pick a ${tmpKindDef.Label.toLowerCase()} —`, Placeholder: `Search ${tmpKindDef.Ph}…`,
				DataProvider: this._kindDataProvider(() => this._appliesKindDef()), ResolveValue: this._kindResolveValue(() => this._appliesKindDef()),
				OnChange: (pValue) => this.setAppliesPick(pValue),
			});
		if (tmpP && (typeof tmpP.render === 'function')) { tmpP.render(); }
	}

	/** (Re)mount the detail pickers present in the DOM (certify holder multi + certifies-for single). */
	mountPickers()
	{
		const tmpPicker = this._pickerProvider();
		if (!tmpPicker || (typeof tmpPicker.createPicker !== 'function')) { return; }
		this.mountCertifyPicker();
		this.mountAppliesPicker();
	}

	// ── Render (hand-built HTML into the mounts) ─────────────────────────────────

	render()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA.getElement(RAIL_MOUNT).length === 0) { return; }
		if (tmpCA.getElement(RAIL_LIST_MOUNT).length === 0) { tmpCA.assignContent(RAIL_MOUNT, this._railHTML()); }
		else { tmpCA.assignContent(RAIL_LIST_MOUNT, this._railListHTML()); }
		tmpCA.assignContent(DETAIL_MOUNT, this._detailHTML());
		// The detail HTML was just repainted (fresh, empty picker mounts) — (re)mount the pickers into it. render()
		// is targeted ContentAssignment, so the view's onAfterRender (which also mounts) does NOT fire here.
		this.mountPickers();
	}

	_esc(pStr) { return String(pStr == null ? '' : pStr).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar])); }

	_railHTML()
	{
		const tmpState = this.state;
		if (!tmpState.Loaded) { return '<div class="cert-empty">Loading…</div>'; }
		const tmpNew = this.canEdit() ? `<button class="cert-newtype-btn" onclick="_Pict.providers.Certifications.showNewType()"><i class="fas fa-plus"></i> New certification type</button>` : '';
		return `<input class="cert-rail-search" type="text" autocomplete="off" spellcheck="false" placeholder="Search certification types…" value="${this._esc(tmpState.TypeSearch || '')}" oninput="_Pict.providers.Certifications.setTypeSearch(this.value)" />`
			+ tmpNew
			+ `<div id="Cert-Rail-List" class="cert-rail-list">${this._railListHTML()}</div>`;
	}

	_railListHTML()
	{
		const tmpState = this.state;
		const tmpQuery = String(tmpState.TypeSearch || '').trim().toLowerCase();
		const tmpTypes = (tmpState.Types || []).filter((pType) => !tmpQuery
			|| String(this._typeLabel(pType)).toLowerCase().indexOf(tmpQuery) > -1
			|| String(pType.Verb || '').toLowerCase().indexOf(tmpQuery) > -1
			|| String(pType.Description || '').toLowerCase().indexOf(tmpQuery) > -1);
		if (!tmpTypes.length) { return `<div class="cert-empty">${tmpQuery ? 'No certification types match.' : 'No certification types yet.'}</div>`; }
		return tmpTypes.map((pType) =>
		{
			const tmpActive = (Number(tmpState.SelectedTypeID) === Number(pType.IDCertificationType)) ? ' is-active' : '';
			const tmpSub = this._typeSub(pType);
			return `<div class="cert-rail-item${tmpActive}" onclick="_Pict.providers.Certifications.selectType(${Number(pType.IDCertificationType)})"><div class="cert-rail-name">${this._esc(this._typeLabel(pType))}</div>${tmpSub ? `<div class="cert-rail-sub">${this._esc(tmpSub)}</div>` : ''}</div>`;
		}).join('');
	}

	_detailHTML()
	{
		const tmpState = this.state;
		if (tmpState.ShowNewType) { return this._newTypeHTML(); }
		if (!tmpState.SelectedTypeID || !tmpState.SelectedType) { return '<div class="cert-detail-empty">Pick a certification type on the left to certify people or things and set what it applies to — or create a new type.</div>'; }
		const tmpEditable = this.canEdit();
		const tmpType = tmpState.SelectedType;
		return `<div class="cert-detail-head"><div class="cert-detail-title">${this._esc(this._typeLabel(tmpType))}</div>`
			+ `<div class="cert-detail-sub">${this._esc([ tmpType.Verb, tmpType.Hash ].filter((pBit) => pBit).join(' · '))}</div>`
			+ (tmpType.Description ? `<p class="cert-detail-desc">${this._esc(tmpType.Description)}</p>` : '') + `</div>`
			+ this._certifyHTML(tmpEditable)
			+ this._holdersHTML(tmpEditable)
			+ this._appliesToHTML(tmpEditable);
	}

	_kindPlural(pKind) { const tmpDef = HOLDER_KIND_BY_TYPE[pKind]; if (!tmpDef) { return pKind || 'entities'; } return (pKind === 'Equipment') ? 'Equipment' : `${tmpDef.Label}s`; }

	_certifyKindOptions()
	{
		const tmpKind = this.state.CertifyKind;
		return this._certifyKindChoices().map((pK) => { const tmpDef = HOLDER_KIND_BY_TYPE[pK]; const tmpLabel = tmpDef ? tmpDef.Label : pK; return `<option value="${pK}"${tmpKind === pK ? ' selected' : ''}>${this._esc(tmpLabel)}</option>`; }).join('');
	}

	_certifyHTML(pEditable)
	{
		const tmpState = this.state;
		if (!pEditable) { return '<div class="cert-section"><div class="cert-section-head">Certify</div><p class="cert-note">You can view certifications here, but adding them needs the Create-certification permission.</p></div>'; }
		const tmpNotice = tmpState.CertifyNotice ? `<div class="cert-notice">${this._esc(tmpState.CertifyNotice)}</div>` : '';
		const tmpError = tmpState.CertifyError ? `<div class="cert-error">${this._esc(tmpState.CertifyError)}</div>` : '';
		const tmpBusy = tmpState.CertifySaving ? '<span class="cert-busy">certifying…</span>' : '';
		// A type configured for a single holder kind hides the dropdown (the "simplified interface"); many kinds → dropdown.
		const tmpChoices = this._certifyKindChoices();
		const tmpKindRow = (tmpChoices.length <= 1)
			? `<div class="cert-kind"><span>Certifying</span><span class="cert-kind-fixed">${this._esc(this._kindPlural(tmpChoices[0]))}</span></div>`
			: `<label class="cert-kind"><span>Certify</span><select class="cert-kind-select" onchange="_Pict.providers.Certifications.setCertifyKind(this.value)">${this._certifyKindOptions()}</select></label>`;
		const tmpCfg = tmpState.TypeConfig;
		const tmpDefPreset = (tmpCfg && (tmpCfg.DefaultValidityYears > 0)) ? `<button class="cert-preset cert-preset-default" onclick="_Pict.providers.Certifications.applyDatePreset(${tmpCfg.DefaultValidityYears})">+${tmpCfg.DefaultValidityYears} yr (default)</button>` : '';
		return `<div class="cert-section"><div class="cert-section-head">Certify</div>`
			+ `<p class="cert-note">Choose what you're certifying, set the dates once (optional), pick one or more, and certify them all at once — each gets its own certification record.</p>`
			+ tmpKindRow
			+ `<div class="cert-dates">`
			+ `<label class="cert-date"><span>Certified on</span><input type="date" value="${this._esc(tmpState.CertOn)}" onchange="_Pict.providers.Certifications.setCertDate('On', this.value)"></label>`
			+ `<label class="cert-date"><span>Start</span><input type="date" value="${this._esc(tmpState.CertStart)}" onchange="_Pict.providers.Certifications.setCertDate('Start', this.value)"></label>`
			+ `<label class="cert-date"><span>End</span><input type="date" value="${this._esc(tmpState.CertEnd)}" onchange="_Pict.providers.Certifications.setCertDate('End', this.value)"></label>`
			+ `<span class="cert-presets">${tmpDefPreset}<button class="cert-preset" onclick="_Pict.providers.Certifications.applyDatePreset(1)">+1 yr</button><button class="cert-preset" onclick="_Pict.providers.Certifications.applyDatePreset(5)">+5 yr</button></span>`
			+ `</div>`
			+ `<label class="cert-notes"><span>Notes</span><input type="text" value="${this._esc(tmpState.Notes)}" placeholder="(optional)" onchange="_Pict.providers.Certifications.setNotes(this.value)"></label>`
			+ `<div class="cert-add-row"><div id="Cert-HolderPicker" class="cert-userpicker"></div><button class="cert-btn cert-btn-primary" onclick="_Pict.providers.Certifications.certifySelectedAsync()">Certify selected</button>${tmpBusy}</div>`
			+ tmpNotice + tmpError + `</div>`;
	}

	_holdersHTML(pEditable)
	{
		const tmpState = this.state;
		this._holdersEditable = pEditable;   // remembered so a body-only repaint keeps the remove buttons in the right mode
		return `<div class="cert-section"><div class="cert-section-head">Current holders</div>`
			+ `<input class="cert-holder-search" type="text" autocomplete="off" spellcheck="false" placeholder="Search holders by name…" value="${this._esc(tmpState.HolderSearch || '')}" oninput="_Pict.providers.Certifications.setHolderSearch(this.value)" />`
			+ `<div id="Cert-Holders-Body" class="cert-holders-body">${this._holdersBodyHTML(pEditable)}</div></div>`;
	}

	/** Repaint just the holders body (count + pager + rows) so the search box keeps focus + the pickers don't remount. */
	_renderHoldersBody()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA.getElement('#Cert-Holders-Body').length > 0) { tmpCA.assignContent('#Cert-Holders-Body', this._holdersBodyHTML(this._holdersEditable)); }
		else { this.render(); }
	}

	_holdersBodyHTML(pEditable)
	{
		const tmpState = this.state;
		if (tmpState.HoldersLoading) { return '<p class="cert-note">Loading…</p>'; }
		const tmpSearching = (String(tmpState.HolderSearch || '').trim().length >= 2);
		const tmpRows = (tmpState.Holders || []).map((pHolder) => this._holderRowHTML(pHolder, pEditable)).join('');
		if (!tmpRows) { return `<p class="cert-note">${tmpSearching ? 'No holders match your search.' : 'No one holds this certification yet.'}</p>`; }
		let tmpMeta;
		if (tmpSearching)
		{
			tmpMeta = `<div class="cert-holders-meta"><span class="cert-count">${tmpState.Holders.length} matching holder${tmpState.Holders.length === 1 ? '' : 's'}</span></div>`;
		}
		else
		{
			const tmpTotal = tmpState.HolderTotalCount;
			const tmpPages = Math.max(1, Math.ceil(tmpTotal / tmpState.HolderPageSize));
			const tmpPage = tmpState.HolderPage;
			const tmpFrom = (tmpState.Holders.length ? (tmpPage * tmpState.HolderPageSize + 1) : 0);
			const tmpTo = tmpPage * tmpState.HolderPageSize + tmpState.Holders.length;
			const tmpPager = (tmpPages > 1)
				? `<span class="cert-pager"><button class="cert-page-btn"${tmpPage <= 0 ? ' disabled' : ''} onclick="_Pict.providers.Certifications.holderPrevPage()">‹ Prev</button><span class="cert-page-ind">Page ${tmpPage + 1} of ${tmpPages}</span><button class="cert-page-btn"${tmpPage >= (tmpPages - 1) ? ' disabled' : ''} onclick="_Pict.providers.Certifications.holderNextPage()">Next ›</button></span>`
				: '';
			tmpMeta = `<div class="cert-holders-meta"><span class="cert-count">${tmpTotal} holder${tmpTotal === 1 ? '' : 's'}${(tmpPages > 1) ? ` · showing ${tmpFrom}–${tmpTo}` : ''}</span>${tmpPager}</div>`;
		}
		return tmpMeta + tmpRows;
	}

	_holderKindLabel(pType) { const tmpDef = HOLDER_KIND_BY_TYPE[pType]; return tmpDef ? tmpDef.Label : (pType || 'Entity'); }

	_holderRowHTML(pHolder, pEditable)
	{
		const tmpState = this.state;
		const tmpName = tmpState.HolderNames[`${pHolder.EntityType}:${pHolder.IDEntity}`] || (`${pHolder.EntityType || 'Entity'} #${pHolder.IDEntity}`);
		const tmpKindBadge = (pHolder.EntityType && (pHolder.EntityType !== 'User')) ? `<span class="cert-kind-badge">${this._esc(this._holderKindLabel(pHolder.EntityType))}</span>` : '';
		const tmpRange = (pHolder.Start || pHolder.End) ? `${pHolder.Start || '—'} → ${pHolder.End || '—'}` : 'no dates';
		const tmpRemove = pEditable ? `<button class="cert-x" title="Remove" onclick="_Pict.providers.Certifications.removeHolderAsync(${pHolder.IDCertification}, ${pHolder.IDJoin})"><i class="fas fa-xmark"></i></button>` : '';
		return `<div class="cert-holder"><div class="cert-holder-main"><span class="cert-holder-name">${this._esc(tmpName)}</span>${tmpKindBadge}${this._holderStatusBadge(pHolder)}</div><div class="cert-holder-dates">${this._esc(tmpRange)}</div>${tmpRemove}</div>`;
	}

	_holderStatusBadge(pHolder)
	{
		if (!pHolder.End && !pHolder.Start) { return ''; }
		const tmpToday = new Date().toISOString().slice(0, 10);
		if (pHolder.End && (pHolder.End < tmpToday)) { return '<span class="cert-badge cert-badge-exp">expired</span>'; }
		if (pHolder.Start && (pHolder.Start > tmpToday)) { return '<span class="cert-badge cert-badge-future">not yet active</span>'; }
		return '<span class="cert-badge cert-badge-active">active</span>';
	}

	_appliesKindOptions()
	{
		const tmpKind = this.state.AppliesKind;
		return this._appliesKindChoices().map((pK) => { const tmpDef = HOLDER_KIND_BY_TYPE[pK]; const tmpLabel = tmpDef ? tmpDef.Label : pK; return `<option value="${pK}"${tmpKind === pK ? ' selected' : ''}>${this._esc(tmpLabel)}</option>`; }).join('');
	}

	_appliesToHTML(pEditable)
	{
		const tmpState = this.state;
		// distinct kinds present, ordered: dropdown kinds first, then any extras (e.g. Customer) so nothing is hidden
		const tmpPresent = [];
		(tmpState.AppliesTo || []).forEach((pRow) => { if (tmpPresent.indexOf(pRow.EntityType) === -1) { tmpPresent.push(pRow.EntityType); } });
		const tmpOrdered = APPLIES_TO_KIND_LIST.filter((pK) => tmpPresent.indexOf(pK) > -1).concat(tmpPresent.filter((pK) => APPLIES_TO_KIND_LIST.indexOf(pK) === -1));
		const tmpGroups = tmpOrdered.map((pType) =>
		{
			const tmpLabel = HOLDER_KIND_BY_TYPE[pType] ? HOLDER_KIND_BY_TYPE[pType].Label : pType;
			const tmpChips = (tmpState.AppliesTo || []).filter((pRow) => pRow.EntityType === pType).map((pRow) =>
			{
				const tmpName = tmpState.AppliesToNames[`${pType}:${Number(pRow.IDEntity)}`] || `${pType} #${pRow.IDEntity}`;
				const tmpRemove = pEditable ? `<button class="cert-x" title="Remove" onclick="_Pict.providers.Certifications.removeAppliesToAsync(${Number(pRow.IDCertificationTypeCertifiedEntityPolyJoin)})"><i class="fas fa-xmark"></i></button>` : '';
				return `<div class="cert-chip">${this._esc(tmpName)}${tmpRemove}</div>`;
			}).join('');
			return `<div class="cert-applies-group"><div class="cert-applies-head">${this._esc(tmpLabel)}</div><div class="cert-chips">${tmpChips || '<span class="cert-note">None</span>'}</div></div>`;
		}).join('');
		const tmpApChoices = this._appliesKindChoices();
		const tmpApKindControl = (tmpApChoices.length <= 1)
			? `<span class="cert-applies-fixed">${this._esc((HOLDER_KIND_BY_TYPE[tmpApChoices[0]] || { Label: tmpApChoices[0] }).Label)}</span>`
			: `<select class="cert-applies-select" onchange="_Pict.providers.Certifications.setAppliesKind(this.value)">${this._appliesKindOptions()}</select>`;
		const tmpAdd = pEditable
			? `<div class="cert-applies-add"><span class="cert-applies-lbl">Kind</span>${tmpApKindControl}<div id="Cert-AppliesPicker" class="cert-entpicker"></div><button class="cert-btn" onclick="_Pict.providers.Certifications.addAppliesToAsync()">Add</button></div>`
			: '';
		const tmpError = tmpState.AppliesToError ? `<div class="cert-error">${this._esc(tmpState.AppliesToError)}</div>` : '';
		return `<div class="cert-section"><div class="cert-section-head">Certifies for</div>`
			+ `<p class="cert-note">Which entities this certification type applies to — materials, labs, tests, equipment…</p>${tmpAdd}${tmpGroups}${tmpError}</div>`;
	}

	_newTypeHTML()
	{
		const tmpState = this.state;
		const tmpError = tmpState.NewTypeError ? `<div class="cert-error">${this._esc(tmpState.NewTypeError)}</div>` : '';
		const tmpBusy = tmpState.NewTypeSaving ? '<span class="cert-busy">saving…</span>' : '';
		return `<div class="cert-detail-head"><div class="cert-detail-title">New certification type</div></div>`
			+ `<div class="cert-section">`
			+ `<label class="cert-field"><span>Name</span><input type="text" value="${this._esc(tmpState.NewTypeName)}" placeholder="e.g. Asphalt Sampling" oninput="_Pict.providers.Certifications.setNewTypeField('Name', this.value)"></label>`
			+ `<label class="cert-field"><span>Verb</span><input type="text" value="${this._esc(tmpState.NewTypeVerb)}" placeholder="e.g. Collect Sample" oninput="_Pict.providers.Certifications.setNewTypeField('Verb', this.value)"></label>`
			+ `<label class="cert-field"><span>Hash / code</span><input type="text" value="${this._esc(tmpState.NewTypeHash)}" placeholder="(optional short code)" oninput="_Pict.providers.Certifications.setNewTypeField('Hash', this.value)"></label>`
			+ `<label class="cert-field"><span>Description</span><textarea rows="2" oninput="_Pict.providers.Certifications.setNewTypeField('Description', this.value)">${this._esc(tmpState.NewTypeDescription)}</textarea></label>`
			+ `<div class="cert-add-row"><button class="cert-btn cert-btn-primary" onclick="_Pict.providers.Certifications.createTypeAsync()">Create type</button><button class="cert-btn cert-btn-ghost" onclick="_Pict.providers.Certifications.selectType(0)">Cancel</button>${tmpBusy}</div>`
			+ tmpError + `</div>`;
	}
}

module.exports = CertificationsProvider;
module.exports.default_configuration =
{
	ProviderIdentifier: 'Certifications',
	AutoInitialize: true,
	AutoInitializeOrdinal: 40,
};
